import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPromptsByType } from '../store/apiActions';
import { setCategoryPrompts } from '../store/actions';

/**
 * Custom hook to fetch and cache prompts by type (personal | suggested).
 *
 * Responsibilities:
 *  - Reads from Redux first (cache hit → no API call)
 *  - Dispatches fetchPromptsByType only on a cache miss
 *  - Persists result back to Redux via setCategoryPrompts
 *  - Exposes loading / error from the apiReducer slice
 *  - Controlled via `enabled` so modal-based consumers only fetch when open
 *
 * @param {string} type - 'personal' | 'suggested'
 * @param {{ enabled?: boolean, force?: boolean }} options
 * @returns {{ prompts: Array, loading: boolean, error: string|null, refetch: Function }}
 */
const usePrompts = (type, { enabled = true, force = false } = {}) => {
  const dispatch = useDispatch();

  const cached = useSelector(
    (state) => state?.state?.categoryPrompts?.[type] || []
  );
  const apiState = useSelector((state) => state?.api?.prompts);
  const loading = apiState?.loading ?? false;
  const error = apiState?.error ?? null;

  const fetchPrompts = useCallback(async (isForced = false) => {
    if (!type) return;

    // Cache hit — skip the API call unless forced
    if (cached.length > 0 && !isForced) return;

    const result = await dispatch(fetchPromptsByType({ type }));
    const prompts = result?.data || [];

    if (Array.isArray(prompts)) {
      dispatch(setCategoryPrompts({ type, data: prompts }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, type, cached.length]);

  useEffect(() => {
    if (!enabled) return;
    fetchPrompts(force);
  }, [enabled, fetchPrompts, force]);

  return { prompts: cached, loading, error, refetch: fetchPrompts };
};

export default usePrompts;
